import { Settings as TLSettings, SidebarItem } from '@treedis/tour-loader';

import { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { captionsEnabledState, gameSoundOnState, gameSoundVolumeState } from 'store';
import { Tour } from '@/_models/Tour';
import { Game } from '@/_models/Game';
import { GameStatus } from '@/components/types';

import { useGame, useLanguage } from '@/hooks/index';

import { Points } from './Points';
import { Time } from './Time';
import { Volume } from './Volume';
import GameAnalyticService from '@/components/tour/game/services/GameAnalyticService';
import { WhiteLabel } from '@/_models/Common';
import { AudioModule } from '../../../AudioModule';

type Props = {
  initialDuplicates: any[];
  tour: Tour;
  translation: Record<string, string>;
  username: string;
  game: Game;
  analytic: GameAnalyticService;
  gameStatus: GameStatus;
  gamePoints: number;
  isDrawerOpen: boolean;
  whiteLabel: WhiteLabel;
  onEndExperience: () => void;
  mainPlaying: boolean;
};

const audioHandler = AudioModule.getInstance();

export const Settings = ({
  initialDuplicates,
  whiteLabel,
  tour,
  translation,
  username,
  game,
  analytic,
  gameStatus,
  gamePoints,
  isDrawerOpen,
  onEndExperience,
  mainPlaying,
}: Props) => {
  const { languageOptions, defaultLanguage, handleChange } = useLanguage({
    initialDuplicates,
    tourSlug: tour?.slug,
  });
  const { gameTime } = useGame({ game, analytic, gameStatus, onEndExperience });

  const [volume, setVolume] = useRecoilState<number>(gameSoundVolumeState);
  const [captionsEnabled, setCaptionsEnabled] = useRecoilState<boolean>(captionsEnabledState);
  const [soundOn, setSoundOn] = useRecoilState(gameSoundOnState);
  const [showReplay, setShowReplay] = useState(false);

  useEffect(() => {
    const { volumeHandler } = audioHandler;
    if (soundOn) {
      volumeHandler(volume);
    } else {
      volumeHandler(0);
    }
  }, [volume, soundOn]);

  const onEnded = useCallback(() => {
    setShowReplay(true);
  }, []);

  useEffect(() => {
    const audioObj = audioHandler.getFirstMusic();
    const audio: HTMLAudioElement | undefined = audioObj?.audio;
    if (audio) {
      if (audio.currentTime === 0) {
        setShowReplay(true);
      }

      if (audioObj.priority === 3) {
        setShowReplay(false);
      }

      audio.addEventListener('ended', onEnded);
    }

    return () => {
      audio?.removeEventListener('ended', onEnded);
    };
  }, [onEnded, mainPlaying]);

  const handleReplay = useCallback(() => {
    const { audio } = audioHandler.getFirstMusic();
    if (audio) {
      audio.play();
      setShowReplay(false);
    }
  }, []);

  const { settings: settingsLabel = 'Settings' } = translation;
  return (
    <SidebarItem
      isOpen={isDrawerOpen}
      popupLabel={settingsLabel}
      button={{
        label: settingsLabel,
        tooltip: settingsLabel,
        icon: 'fa-cog',
      }}
    >
      <TLSettings
        translation={translation}
        whiteLabel={whiteLabel}
        captions={{
          value: captionsEnabled,
          onToggle: () => setCaptionsEnabled((prev) => !prev),
        }}
        language={{
          options: languageOptions,
          default: defaultLanguage,
          onChange: handleChange,
        }}
        termsOfUse={{
          file:
            tour.termsUrl ||
            (translation.termFile
              ? `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/termFile.pdf`
              : ''),
          label: translation?.termsOfUse || 'Terms of Use',
        }}
        help={{
          show: true,
          label: translation?.tips || 'Tips',
        }}
        game={{
          label: game.name,
          user: username,
        }}
        components={{
          time: <Time gameTime={gameTime} />,
          points: game.progressOn ? (
            <Points scoreType={game.scoreType} game={game} gamePoints={gamePoints} />
          ) : (
            <></>
          ),
          volume: (
            <Volume
              volume={{ value: volume, onChange: (val) => setVolume(val) }}
              mute={{ value: soundOn, onToggle: () => setSoundOn((prev) => !prev) }}
              showReplay={showReplay}
              onReplay={handleReplay}
              translation={translation}
            />
          ),
        }}
      />
    </SidebarItem>
  );
};
